/**
 * 사이드바(nav) 미노출 모듈 / 업무유형 — SSOT.
 *
 * 프로비저닝은 ON(모듈 데이터·업무유형 보존)이되 사이드바 및 그와 정렬되는 화면(워크플로우 편집기
 * 모듈 드롭다운 등)에서 노출만 제외함. AppShell·WorkflowBuilderPage 등 여러 곳이 같은 기준을
 * 따르도록 한 곳에서 정의(하드코딩 중복 금지).
 *
 * · 설계관리(DSN): 목업 20260720 사이드바에 독립 "설계관리" 메뉴 없음(프로젝트관리 모듈 내부 탭
 *   DHF·설계관리요소). 프로비저닝 "프로젝트관리" 토글(=DSN)은 ON 유지, nav 노출만 제외.
 */
export const NAV_HIDDEN_MODULES = new Set<string>(["DSN"]);

/**
 * 설정 › 메뉴 권한 탭에 행을 두지 않는 모듈 — SSOT.
 *
 * · 공지사항(NOTICE): 열람은 구성원 전원, 등록·수정·삭제·발송은 관리자만(서버 modules.write_admin_only).
 *   메뉴 권한으로 위임할 수 없으므로(사용자 결정 2026-10-26, docs/ui-spec/notice/green.md 결정 2) 권한 행을 만들지 않음.
 *   행이 생기면 조회(r)를 끈 사용자에게 사이드바 메뉴만 사라져 "열람은 전원" 원칙과 어긋남.
 */
export const PERM_MENU_HIDDEN_MODULES = new Set<string>(["NOTICE"]);

/**
 * 사이드바(nav) 미노출 업무유형(typeCode) — SSOT.
 *
 * 모듈은 ON이지만 사이드바 리프 항목에서 제외되는 업무유형.
 * · DOC_LIBRARY: 문서 라이브러리(폴더 트리 전용, 독립 메뉴 없음)
 * · DOC_QUALITY_RECORD: 품질기록(DOC_RECORD 탭 내부 표시, 독립 항목 없음)
 * · QUALIFICATION·TRN_CERT: 옛 자격인증관리·사내 자격(V140 — 자격인증관리대장으로 교체, 유형·데이터는 보존)
 * · TRN_CERT_BG: 인증서 배경(V140 — 자격인증관리대장의 배경 등록 모달 전용)
 * · PRJ·PRJ_TASK: 프로젝트 대표 아이템·업무(V137) — 프로젝트 관리 드로어에서만 다룸
 * · SUP_CERT·SUP_IQC·SUP_SCAR·SUP_SCN·SUP_REEVAL·SUP_SC: 공급자 하위 기록(V250) — 공급자 관리 화면의 탭·드로어에서만 다룸
 */
export const NAV_HIDDEN_TYPES = new Set<string>([
  "DOC_LIBRARY",
  "DOC_QUALITY_RECORD",
  "QUALIFICATION",
  "TRN_CERT",
  "TRN_CERT_BG",
  "PRJ",
  "PRJ_TASK",
  "SUP_CERT",
  "SUP_IQC",
  "SUP_SCAR",
  "SUP_SCN",
  "SUP_REEVAL",
  "SUP_SC",
]);

/**
 * 워크플로우 빌더 업무 선택에서 빼는 업무유형(typeCode) — 사이드바 미노출 유형(NAV_HIDDEN_TYPES)에 더해 적용.
 * · TRN_RECORD: 개인별 교육 이력 — 단계 이름이 화면에 나오지 않고 발급 여부는 완료 단계로만 판정
 * · TRN_CERTIFY: 자격인증관리대장 — 워크플로 없음(등록 시 전자서명으로 확정)
 */
export const WORKFLOW_HIDDEN_TYPES = new Set<string>(["TRN_RECORD", "TRN_CERTIFY"]);

/**
 * 워크플로우 편집기에서 숨기는 단계(status_key) — 분류는 그대로 두고 편집 목록에서만 뺌(잠금·집계는 분류·키 기준이라 영향 없음).
 * · wfs-trncur-closed: 교육 반려 종결 '반려'(V240) — 화면에 반려 버튼이 없어 갈 수 없는 단계(옛 반려 건 표시용으로만 남음)
 */
export const WORKFLOW_HIDDEN_STATUS_KEYS = new Set<string>(["wfs-trncur-closed"]);

/**
 * 숨김 하위 기록 유형(NAV_HIDDEN_TYPES)을 설정 › 결재선 관리에서 그 모듈 메뉴 안의 소그룹으로 합치는 모듈 — 공급자 관리(SUP).
 * 하위 기록마다 서명 단계(인증서 검토·로트 판정·SCAR 단계 등)가 있어 결재선 줄이 필요하지만 사이드바에는 따로 메뉴가 없음.
 * 문서관리(DOC_LIBRARY 등)·프로젝트(PRJ) 같은 다른 숨김 유형은 합치지 않음(그 메뉴의 결재선 목록 불변).
 */
export const SUBRECORD_MENU_MODULES = new Set<string>(["SUP"]);

/**
 * 워크플로우(상태머신)가 있는 모듈 집합 — SSOT.
 *
 * 워크플로우 빌더·필드 빌더의 모듈 드롭다운이 같은 모듈 집합을 쓰도록 한 곳에서 정의(하드코딩 중복 금지).
 * 추적성(TRACE)은 상태머신이 없어 제외. 순서는 modules.sort(DB SSOT, 사이드바 동일).
 */
export const WORKFLOW_MODULES = new Set<string>([
  "DOC",
  "CAPA",
  "NC",
  "CHG",
  "RSK",
  "AUD",
  "SUP",
  "TRN",
  "CMP",
  "product",
  "DSN",
]);
